import { lazy, Suspense } from 'react';
import PageNavbar from '../../components/feature/PageNavbar';
import Footer from '../home/components/Footer';
import ScrollToTopButton from '../../components/feature/ScrollToTopButton';
import FreiburgHero from './components/FreiburgHero';
import {
  useSEO,
  generateWebPageSchema,
  generateLocalBusinessSchema,
  generateFAQSchema,
  generateBreadcrumbSchema,
} from '../../utils/seo';

const FreiburgServices = lazy(() => import('./components/FreiburgServices'));
const FreiburgWhyUs = lazy(() => import('./components/FreiburgWhyUs'));
const FreiburgPricing = lazy(() => import('./components/FreiburgPricing'));
const FreiburgFAQ = lazy(() => import('./components/FreiburgFAQ'));
const FreiburgContact = lazy(() => import('./components/FreiburgContact'));

const SectionLoader = () => (
  <div className="py-16 flex items-center justify-center">
    <div className="w-8 h-8 border-4 border-brand-green border-t-transparent rounded-full animate-spin" />
  </div>
);

const faqData = [
  {
    question: 'Erstellen Sie Webseiten für Unternehmen in Freiburg?',
    answer:
      'Ja, absolut! Ich erstelle professionelle Webseiten für Unternehmen in Freiburg im Breisgau und der gesamten Region. Als Webdesigner aus dem nahen Schwarzwald kenne ich den Freiburger Markt sehr gut.',
  },
  {
    question: 'Was kostet Webdesign in Freiburg bei Ihnen?',
    answer:
      'Mein Komplettpaket für Webdesign in Freiburg kostet einmalig 899 €. Darin enthalten sind bis zu 7 Seiten, Responsive Design, SEO-Optimierung für Freiburg, Kontaktformular, SSL-Zertifikat, DSGVO-Absicherung und 2 kostenlose Änderungen.',
  },
  {
    question: 'Wie lange dauert die Erstellung einer Webseite für Freiburg?',
    answer:
      'Ihre Webseite ist in maximal 1 Woche fertig – deutlich schneller als die meisten Freiburger Agenturen.',
  },
  {
    question: 'Bieten Sie auch lokale SEO für Freiburg an?',
    answer:
      'Ja! Lokale SEO-Optimierung für Freiburg ist in jedem Paket enthalten. Ich optimiere Ihre Webseite gezielt für Suchanfragen wie "Webdesign Freiburg" oder "[Ihre Branche] Freiburg".',
  },
];

export default function FreiburgPage() {
  useSEO({
    title: 'Webdesign Freiburg – Professionell, schnell & ab 899 € | BR Webdesign',
    description:
      'Webdesign Freiburg von BR Webdesign: Moderne Webseiten, AI-Chatbots & SEO für Freiburger Unternehmen. Festpreis 899 €, fertig in 1 Woche. Jetzt kostenlos anfragen!',
    keywords:
      'Webdesign Freiburg, Webseite erstellen Freiburg, SEO Freiburg, AI Chatbot Freiburg, Webdesigner Freiburg',
    canonical: '/webdesign-freiburg',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateWebPageSchema(
          'Webdesign Freiburg – Professionell, schnell & ab 899 €',
          'Professionelles Webdesign in Freiburg im Breisgau. Moderne Webseiten mit AI-Chatbots, SEO-Optimierung und DSGVO-Konformität. Festpreis 899 €, fertig in max. 1 Woche.',
          '/webdesign-freiburg'
        ),
        generateLocalBusinessSchema(),
        generateFAQSchema(faqData),
        generateBreadcrumbSchema([
          { name: 'Startseite', url: '/' },
          { name: 'Webdesign Freiburg', url: '/webdesign-freiburg' },
        ]),
        {
          '@context': 'https://schema.org',
          '@type': 'Service',
          name: 'Webdesign Freiburg',
          description:
            'Professionelles Webdesign für Unternehmen in Freiburg im Breisgau. Moderne Webseiten mit AI-Chatbots, SEO und DSGVO-Konformität ab 899 €.',
          provider: {
            '@type': 'LocalBusiness',
            name: 'BR Webdesign & AI Solutions',
            telephone: '+49-162-3651571',
            url: 'https://webdesignki.de',
          },
          areaServed: {
            '@type': 'City',
            name: 'Freiburg im Breisgau',
          },
          offers: {
            '@type': 'Offer',
            price: '899',
            priceCurrency: 'EUR',
            description: 'Komplettpaket Webseite bis 7 Seiten inkl. SEO, DSGVO & 2 Änderungen',
          },
        },
      ],
    },
  });

  return (
    <div className="min-h-screen bg-white">
      <PageNavbar />
      <FreiburgHero />
      <Suspense fallback={<SectionLoader />}>
        <FreiburgServices />
      </Suspense>
      <Suspense fallback={<SectionLoader />}>
        <FreiburgWhyUs />
      </Suspense>
      <Suspense fallback={<SectionLoader />}>
        <FreiburgPricing />
      </Suspense>
      <Suspense fallback={<SectionLoader />}>
        <FreiburgFAQ />
      </Suspense>
      <Suspense fallback={<SectionLoader />}>
        <FreiburgContact />
      </Suspense>
      <Footer />
      <ScrollToTopButton />
    </div>
  );
}
